/* ============================================================
   Cart ETA Refresh — Baby Gold / Broadcast theme
   ------------------------------------------------------------
   Recomputes the "Estimated Delivery" line item properties when
   the cart drawer opens, so the cart shows CURRENT lead times
   instead of whatever was stamped at add-to-cart.

   Ported logic (matches the theme's writer exactly):
   - Message rules come from the #lead-time-message JSON config
     [{ message_tag, message_content, above_or_below_zero }, ...]
   - First product tag with a matching config entry wins, where
     above_zero requires live inventory > 0 and below_zero
     requires live inventory <= 0.
   - Live inventory is read from the theme's existing
     "api-live-cart-info" section ([data-max-inventory]).

   Updates per line item (merged, never replacing other props):
   - "Estimated Delivery"   (customer-visible message)
   - "_check_s_m_trigger"   (writer's mirror of the message)
   - "_default_invent_qty"  (inventory qty at time of check)
   ============================================================ */

(function () {
  'use strict';

  var CONFIG = {
    PRIMARY_KEY: 'Estimated Delivery',
    MIRROR_KEYS: ['_check_s_m_trigger'],
    QTY_KEY: '_default_invent_qty',

    DRAWER_SELECTOR: '#cart-drawer',
    ITEMS_SELECTOR: 'cart-items',
    DRAWER_SECTION_ID: null, // auto-detected (drawer lives in a section group)

    // Line items to skip entirely (apps/add-ons with no ETA).
    SKIP_TITLE_PATTERNS: [/package protection/i, /free returns/i],

    COOLDOWN_MS: 15000,

    // Intercept non-native checkout buttons (e.g. Redo's Checkout+ /
    // Checkout without protection) for a final refresh before checkout.
    // Set to false if any third-party checkout button misbehaves —
    // the tab-refocus trigger below still covers most staleness.
    INTERCEPT_CUSTOM_CHECKOUT_BUTTONS: true,

    // Buttons matching these patterns are never intercepted (their handlers
    // reject synthetic re-clicks). Redo's Checkout+ is one — it passes
    // through untouched and relies on the drawer-open/refocus refreshes.
    CUSTOM_CHECKOUT_EXCLUDE: [/checkout\s*\+/i],

    DEBUG: false // cartEtaRefresh() turns this on for manual runs
  };

  function dbg() {
    if (CONFIG.DEBUG) console.log.apply(console, ['[cart-eta-refresh]'].concat([].slice.call(arguments)));
  }

  var lastRun = 0;
  var running = false;
  var productCache = {};
  var inventoryCache = {};
  var leadTimeMapPromise = null;

  /* ---------------- helpers ---------------- */

  function getJSON(url) {
    return fetch(url, { headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('Fetch failed: ' + url + ' (' + r.status + ')');
      return r.json();
    });
  }

  function getProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = getJSON('/products/' + handle + '.js');
    }
    return productCache[handle];
  }

  function stripHtml(s) {
    return String(s || '').replace(/<[^>]+>/g, '');
  }

  /* ----- lead-time config (#lead-time-message JSON) -----
     Read from the current page if present; otherwise fetch it
     out of a product page once and cache. Same config the
     theme's writer uses, so messages stay in sync with admin. */
  function getLeadTimeMap(fallbackProductUrl) {
    if (leadTimeMapPromise) return leadTimeMapPromise;

    var el = document.getElementById('lead-time-message');
    if (el) {
      try {
        var arr = JSON.parse(el.textContent || el.innerHTML);
        leadTimeMapPromise = Promise.resolve(buildMap(arr));
        return leadTimeMapPromise;
      } catch (e) { /* fall through to fetch */ }
    }

    if (!fallbackProductUrl) return Promise.resolve(null);

    leadTimeMapPromise = fetch(fallbackProductUrl)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var node = doc.getElementById('lead-time-message');
        if (!node) return null;
        return buildMap(JSON.parse(node.textContent || node.innerHTML));
      })
      .catch(function () { return null; });

    return leadTimeMapPromise;

    function buildMap(messages) {
      var map = new Map();
      (messages || []).forEach(function (m) { map.set(m.message_tag, m); });
      return map.size ? map : null;
    }
  }

  /* ----- live inventory via our dedicated api-eta-inventory section -----
     Reads variant.inventory_quantity directly (policy-independent; can be
     negative when oversold). Untracked variants are treated as 0, matching
     the writer's default (Frenzy: `|| '0'` → below_zero / MTO branch).
     Falls back to the theme's api-live-cart-info if the section is missing. */
  function getLiveInventory(handle, variantId) {
    var key = handle + ':' + variantId;
    if (!inventoryCache[key]) {
      var root = (window.theme && theme.routes && theme.routes.root) || '/';
      var base = root + 'products/' + handle + '?variant=' + variantId + '&section_id=';
      inventoryCache[key] = fetch(base + 'api-eta-inventory')
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var node = doc.querySelector('[data-eta-inventory]');
          if (node) {
            if (node.getAttribute('data-tracked') !== 'true') return 0; // untracked → writer default
            return Number(node.getAttribute('data-qty'));
          }
          // Section not installed — fall back to the theme's endpoint.
          return fetch(base + 'api-live-cart-info')
            .then(function (r2) { return r2.text(); })
            .then(function (html2) {
              var doc2 = new DOMParser().parseFromString(html2, 'text/html');
              var n2 = doc2.querySelector('[data-max-inventory]');
              var raw = n2 ? n2.innerHTML.trim() : '';
              return raw === '' ? 0 : Number(raw);
            });
        })
        .catch(function () { return null; });
    }
    return inventoryCache[key];
  }

  /* ----- the ported rules: first matching tag wins ----- */
  function computeMessage(ltMap, tags, count) {
    if (!ltMap || count === null || isNaN(count)) return null;
    for (var i = 0; i < tags.length; i++) {
      var matched = ltMap.get(tags[i]);
      if (!matched) continue;
      var messageText = stripHtml(matched.message_content);
      var above = matched.above_or_below_zero === 'above_zero';
      if ((count > 0 && above) || (count <= 0 && !above)) {
        return messageText;
      }
    }
    return null;
  }

  function changeLine(key, properties) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, properties: properties })
    }).then(function (r) {
      if (r.status === 400 || r.status === 404) {
        // Line key no longer exists (re-keyed by a concurrent update).
        // Not fatal — the next run works from a fresh snapshot.
        dbg('line ' + key + ' is stale (' + r.status + '), skipping');
        return null;
      }
      if (!r.ok) throw new Error('cart/change failed: ' + r.status);
      return r.json();
    });
  }

  /* ----- drawer re-render (Section Rendering API) ----- */
  function detectSectionId() {
    if (CONFIG.DRAWER_SECTION_ID) return CONFIG.DRAWER_SECTION_ID;
    var drawer = document.querySelector(CONFIG.DRAWER_SELECTOR);
    var wrapper = drawer && drawer.closest('[id^="shopify-section-"]');
    if (wrapper) CONFIG.DRAWER_SECTION_ID = wrapper.id.replace('shopify-section-', '');
    return CONFIG.DRAWER_SECTION_ID;
  }

  // NOTE: cartCount is passed through from the /cart.js fetch in run() so we
  // can supply Broadcast's CartCount custom element with the detail.cartCount
  // it expects on "theme:cart:change" — dispatching without it throws inside
  // CartCount.onCartChange (reading .cartCount off an undefined detail).
  function refreshDrawer(cartCount) {
    var sectionId = detectSectionId();
    if (!sectionId) return Promise.resolve();
    return fetch('/?sections=' + sectionId)
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        var html = sections[sectionId];
        if (!html) return;
        // Replace only the items holder so Broadcast's custom elements
        // (<cart-drawer>, <collapsible-elements>) keep their listeners.
        var current = document.querySelector(CONFIG.DRAWER_SELECTOR + ' ' + CONFIG.ITEMS_SELECTOR)
                   || document.querySelector(CONFIG.DRAWER_SELECTOR);
        if (!current) return;
        var tpl = document.createElement('div');
        tpl.innerHTML = html;
        var fresh = tpl.querySelector(CONFIG.ITEMS_SELECTOR) || tpl.querySelector(CONFIG.DRAWER_SELECTOR);
        if (fresh) current.innerHTML = fresh.innerHTML;
        document.dispatchEvent(new CustomEvent('theme:cart:change', {
          bubbles: true,
          detail: { cartCount: cartCount }
        }));
      })
      .catch(function () { /* non-fatal: values are correct on next render */ });
  }

  /* ---------------- main run ---------------- */

  function processItem(item, ltMap) {
    return Promise.all([
      getProduct(item.handle),
      getLiveInventory(item.handle, item.variant_id)
    ]).then(function (results) {
      var product = results[0];
      var count = results[1];
      var fresh = computeMessage(ltMap, product.tags || [], count);
      if (fresh === null) {
        dbg('item "' + item.product_title + '": no rule matched (count=' + count + ')');
        return null;
      }

      dbg('item "' + item.product_title + '": count=' + count + ', computed=' + JSON.stringify(fresh) + ', stored=' + JSON.stringify((item.properties || {})[CONFIG.PRIMARY_KEY]));
      var props = item.properties || {};
      var qtyValue = String(count);
      var unchanged = props[CONFIG.PRIMARY_KEY] === fresh &&
        CONFIG.MIRROR_KEYS.every(function (k) { return props[k] === fresh; }) &&
        (props[CONFIG.QTY_KEY] === undefined || props[CONFIG.QTY_KEY] === qtyValue);

      if (unchanged) return null;

      // MERGE — /cart/change.js replaces properties wholesale, so existing
      // properties (Redo's, Black Crow's, _added_time, etc.) must be carried.
      var merged = Object.assign({}, props);
      merged[CONFIG.PRIMARY_KEY] = fresh;
      CONFIG.MIRROR_KEYS.forEach(function (k) { merged[k] = fresh; });
      if (props[CONFIG.QTY_KEY] !== undefined) merged[CONFIG.QTY_KEY] = qtyValue;

      return { key: item.key, properties: merged };
    }).catch(function (err) {
      dbg('item "' + item.product_title + '" FAILED:', err && err.message || err);
      return null;
    });
  }

  function run() {
    var now = Date.now();
    if (running || now - lastRun < CONFIG.COOLDOWN_MS) { dbg('skipped: running=' + running + ' cooldownRemaining=' + Math.max(0, CONFIG.COOLDOWN_MS - (now - lastRun)) + 'ms'); return; }
    dbg('run started');
    running = true;
    inventoryCache = {}; // inventory must be fresh every run

    return getJSON('/cart.js')
      .then(function (cart) {
        var items = (cart.items || []).filter(function (item) {
          return !CONFIG.SKIP_TITLE_PATTERNS.some(function (re) {
            return re.test(item.product_title || item.title || '');
          });
        });
        dbg('eligible items:', items.length, items.map(function (i) { return i.product_title; }));
        if (!items.length) return null;

        return getLeadTimeMap(items[0].url).then(function (ltMap) {
          dbg('lead-time config:', ltMap ? ltMap.size + ' rules' : 'NOT FOUND');
          if (!ltMap) return null;

          // Process items one at a time — parallel bursts of 20+ requests
          // trip Shopify storefront throttling and fail silently.
          var updates = [];
          return items.reduce(function (chain, item) {
            return chain.then(function () {
              return processItem(item, ltMap).then(function (u) { if (u) updates.push(u); });
            });
          }, Promise.resolve()).then(function () {
            dbg('updates to apply:', updates.length, updates.map(function (u) { return u.properties[CONFIG.PRIMARY_KEY]; }));
            // ...then apply sequentially (parallel cart mutations race).
            return updates.reduce(function (chain, u) {
              return chain.then(function () {
                return changeLine(u.key, u.properties).then(function (r) { dbg('updated line', u.key); return r; });
              });
            }, Promise.resolve()).then(function () {
              dbg('all updates applied' + (updates.length ? ', refreshing drawer' : ' (none needed)'));
              return updates.length ? refreshDrawer(cart.item_count) : null;
            });
          });
        });
      })
      .catch(function (err) { console.warn('[cart-eta-refresh]', err); })
      .finally(function () {
        lastRun = Date.now();
        running = false;
      });
  }

  /* ---------------- triggers ---------------- */

  // 1. Cart drawer open — watch the drawer's class for "is-open".
  //    Selector-proof: doesn't depend on what the cart icon looks like.
  function watchDrawer() {
    var drawer = document.querySelector(CONFIG.DRAWER_SELECTOR);
    if (!drawer) return false;
    var wasOpen = drawer.classList.contains('is-open');
    new MutationObserver(function () {
      var isOpen = drawer.classList.contains('is-open');
      if (isOpen && !wasOpen) setTimeout(run, 300);
      wasOpen = isOpen;
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    return true;
  }
  if (!watchDrawer()) {
    document.addEventListener('DOMContentLoaded', watchDrawer);
  }

  // Manual hook for testing from the console: cartEtaRefresh()
  window.cartEtaRefresh = function () { CONFIG.DEBUG = true; lastRun = 0; run(); };

  // 2. After any add-to-cart (theme or app AJAX).
  var _fetch = window.fetch;
  window.fetch = function () {
    var url = typeof arguments[0] === 'string' ? arguments[0] : (arguments[0] && arguments[0].url) || '';
    var isCartAdd = url.indexOf('/cart/add') !== -1;
    var result = _fetch.apply(this, arguments);
    if (isCartAdd) result.then(function () { setTimeout(run, 600); });
    return result;
  };

  // 3. Cart page — run once on load.
  if (location.pathname === '/cart') {
    document.addEventListener('DOMContentLoaded', run);
  }

  // 4. Final refresh on checkout click — properties freeze into the order
  //    at this moment, so do one last pass before leaving the cart.
  //    Hard 3s cap: never block checkout on a slow run.
  var checkoutPassthrough = false;
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form.matches('[data-cart-form], form[action^="/cart"]')) return;
    var submitter = e.submitter;
    if (!submitter || submitter.name !== 'checkout') return;
    if (checkoutPassthrough) { checkoutPassthrough = false; return; }

    e.preventDefault();
    dbg('checkout intercepted — final refresh (max 3s)');

    var done = false;
    function proceed() {
      if (done) return;
      done = true;
      checkoutPassthrough = true;
      form.requestSubmit(submitter); // re-submits WITH the checkout button
    }
    setTimeout(proceed, 3000); // failsafe: checkout always continues

    lastRun = 0; // bypass cooldown for this critical pass
    var p = run();
    if (p && p.finally) {
      p.finally(proceed);
    } else {
      // A run was already in flight; give it a moment, then go.
      setTimeout(proceed, 1500);
    }
  }, true);

  // 5. Tab refocus — covers "opened drawer, left for an hour, came back".
  //    The customer must refocus the tab before they can click anything,
  //    so a refresh here closes most of the staleness window risk-free.
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') run();
  });

  // 6. Custom checkout buttons (Redo's Checkout+ etc.) — capture-phase
  //    click interception with a passthrough re-dispatch after refreshing.
  document.addEventListener('click', function (e) {
    if (!CONFIG.INTERCEPT_CUSTOM_CHECKOUT_BUTTONS) return;
    var btn = e.target.closest('button, a, [role="button"]');
    if (!btn) return;
    if (btn.dataset.etaPassthrough) { delete btn.dataset.etaPassthrough; return; }
    if (btn.name === 'checkout') return; // native path: handled by submit interceptor

    var inCartContext = btn.closest(CONFIG.DRAWER_SELECTOR) || btn.closest('[data-cart-form]') || location.pathname === '/cart';
    if (!inCartContext) return;
    var label = (btn.textContent || '').trim();
    if (!/checkout/i.test(label)) return;
    if (CONFIG.CUSTOM_CHECKOUT_EXCLUDE.some(function (re) { return re.test(label); })) {
      dbg('checkout button excluded from interception:', label);
      return;
    }

    e.preventDefault();
    e.stopImmediatePropagation();
    dbg('custom checkout button intercepted — final refresh (max 3s):', (btn.textContent || '').trim());

    var done = false;
    function proceed() {
      if (done) return;
      done = true;
      btn.dataset.etaPassthrough = '1';
      btn.click(); // re-dispatch; Redo's own handler takes over
    }
    setTimeout(proceed, 3000);
    lastRun = 0;
    var p = run();
    if (p && p.finally) { p.finally(proceed); } else { setTimeout(proceed, 1500); }
  }, true);

  // 7. Frenzy's own post-cart-action hook — fires after ANY Frenzy cart
  //    action (quick add, bundles, PDP carousels, and newer widgets like
  //    the upsell block), regardless of whether the widget's request goes
  //    through fetch, XHR, or opens the drawer via a different mechanism.
  //    Wrapped, not replaced, so Frenzy's own updateCart() still runs.
  var _frenzyCartCallBack = window.frenzyCartCallBack;
  window.frenzyCartCallBack = function () {
    if (typeof _frenzyCartCallBack === 'function') _frenzyCartCallBack.apply(this, arguments);
    setTimeout(run, 600);
  };
})();